import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { supabase } from '../../lib/supabaseClient';
import { Loader2, Building, Mail, Phone, Globe, MapPin, AlertCircle } from 'lucide-react';

const ClientMyOrganisation = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [stats, setStats] = useState({
    activeRFQs: 0,
    totalApplications: 0,
  });

  useEffect(() => {
    fetchOrganisationDetails();
  }, []);

  const fetchOrganisationDetails = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('Not authenticated');
        return;
      }

      // Get client contact info with client details
      const { data: contact, error: contactError } = await supabase
        .from('client_contacts')
        .select('*, client:clients(*)')
        .eq('user_id', user.id)
        .single();

      if (contactError) {
        if (contactError.code === 'PGRST116') {
          setError('No organization found. Please contact support.');
        } else {
          throw contactError;
        }
        return;
      }

      if (contact?.client) {
        setClientData(contact.client);

        // Get RFQs count
        const { count: rfqCount } = await supabase
          .from('rfqs')
          .select('*', { count: 'exact', head: true })
          .eq('client_id', contact.client.id)
          .eq('status', 'Open');

        // Get applications count for this client's RFQs
        const { count: appCount } = await supabase
          .from('applications')
          .select('rfq:rfqs!inner(client_id)', { count: 'exact', head: true })
          .eq('rfq.client_id', contact.client.id);

        setStats({
          activeRFQs: rfqCount || 0,
          totalApplications: appCount || 0,
        });
      }
    } catch (err) {
      console.error('Error fetching organisation details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">My Organisation</h2>
          <p className="text-muted-foreground mt-1">View your organisation details</p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">My Organisation</h2>
          <p className="text-muted-foreground mt-1">View your organisation details</p>
        </div>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No organisation data available</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">My Organisation</h2>
        <p className="text-muted-foreground mt-1">View your organisation details</p>
      </div>

      {/* Organisation Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active RFQs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeRFQs}</div>
            <p className="text-xs text-muted-foreground">Open job requests</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalApplications}</div>
            <p className="text-xs text-muted-foreground">All-time applications</p>
          </CardContent>
        </Card>
      </div>

      {/* Organisation Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-primary" />
            <CardTitle>{clientData.name}</CardTitle>
          </div>
          <CardDescription>Organisation Information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {clientData.industry && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Industry</p>
                <p className="text-base">{clientData.industry}</p>
              </div>
            )}

            {clientData.status && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <Badge variant={clientData.status === 'Active' ? 'success' : 'secondary'}>
                  {clientData.status}
                </Badge>
              </div>
            )}

            {clientData.email && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Email</p>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${clientData.email}`} className="text-base text-primary hover:underline">
                    {clientData.email}
                  </a>
                </div>
              </div>
            )}

            {clientData.phone && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Phone</p>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${clientData.phone}`} className="text-base text-primary hover:underline">
                    {clientData.phone}
                  </a>
                </div>
              </div>
            )}

            {clientData.website && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Website</p>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={clientData.website.startsWith('http') ? clientData.website : `https://${clientData.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-primary hover:underline"
                  >
                    {clientData.website}
                  </a>
                </div>
              </div>
            )}

            {clientData.address && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-muted-foreground mb-1">Address</p>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <p className="text-base">{clientData.address}</p>
                </div>
              </div>
            )}
          </div>

          {clientData.notes && (
            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-2">Notes</p>
              <p className="text-base text-muted-foreground">{clientData.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          To update organisation details, please contact your Appsytec account manager.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ClientMyOrganisation;
